import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import QrScanner from 'qr-scanner';
import { X } from 'lucide-react';
import { useAppDispatch } from '../store/hooks';
import { closeScanner } from '../store/slices/navigationSlice';
import { ExhibitId } from '../store/slices/exhibitSlice';
import { useOrientation } from '../hooks/useDeviceDetection';

interface QrScannerModalProps {
    isOpen: boolean;
    onClose: () => void;
    showCloseButton?: boolean;
}

export default function QrScannerModal({ isOpen, onClose, showCloseButton = true }: QrScannerModalProps) {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [scanner, setScanner] = useState<QrScanner | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [scanning, setScanning] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const orientation = useOrientation();

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    // Auto-close modal when device is rotated to landscape
    useEffect(() => {
        if (orientation === 'landscape' && isOpen) {
            console.log('QR Scanner: Auto-closing due to landscape orientation');
            onClose();
        }
    }, [orientation, isOpen, onClose]);

    // Handle modal visibility with animation timing
    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            // Start the entrance animation after a small delay
            setTimeout(() => setIsVisible(true), 10);
        } else {
            setIsVisible(false);
            // Hide the modal after animation completes
            const timer = setTimeout(() => {
                setShouldRender(false);
            }, 300); // Match the transition duration
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    const handleAnimatedClose = useCallback(() => {
        setIsVisible(false);
        // Wait for animation to complete before calling onClose
        setTimeout(() => {
            onClose();
        }, 300); // Match the transition duration
    }, [onClose]);
    
    const handleScan = useCallback(async (data: string) => {
        // Stop scanning once we get a result
        if (scanner) {
            scanner.stop();
        }
        
        // Debug: Log the actual QR code data
        console.log('QR Code Data:', data);
        
        // Process the QR code data
        let exhibitId: ExhibitId | null = null;
        
        try {
            // Try to parse as URL first (both timecapsule:// and https://)
            const url = new URL(data);
            
            if (url.protocol === 'timecapsule:') {
                const pathParts = url.pathname.split('/').filter(Boolean);
                
                if (pathParts[0] === 'exhibit' && pathParts[1]) {
                    exhibitId = pathParts[1] as ExhibitId;
                }
            } else if (url.protocol === 'https:' || url.protocol === 'http:') {
                // Handle HTTPS URLs like: https://timecapsule.untitledproject.sg/staging/exhibit/orang-laut
                const pathParts = url.pathname.split('/').filter(Boolean);
                
                // Find 'exhibit' in the path and get the next part
                const exhibitIndex = pathParts.findIndex(part => part === 'exhibit');
                if (exhibitIndex !== -1 && pathParts[exhibitIndex + 1]) {
                    exhibitId = pathParts[exhibitIndex + 1] as ExhibitId;
                }
            }
        } catch (urlError) {
            // If it's not a valid URL, try other formats
            console.log('Not a valid URL, trying other formats...');
            
            // Try direct text matching (case insensitive)
            const lowerData = data.toLowerCase().trim();
            const textMappings: Record<string, ExhibitId> = {
                'orang laut': 'orang-laut',
                'orang-laut': 'orang-laut',
                'oranglaut': 'orang-laut',
                'market traders': 'market-traders',
                'market-traders': 'market-traders',
                'markettraders': 'market-traders',
                'early market traders': 'market-traders',
                'badang': 'badang',
                'legend of badang': 'badang',
                'book of myths': 'book-of-myths',
                'book-of-myths': 'book-of-myths',
                'island myths book': 'book-of-myths',
                'sg60': 'sg60',
                'singapore 60': 'sg60',
                'modern singapore': 'sg60',
                'peranakan': 'peranakan-design',
                'peranakan design': 'peranakan-design',
                'peranakan-design': 'peranakan-design',
                'time capsule': 'time-capsule',
                'time-capsule': 'time-capsule',
                'timecapsule': 'time-capsule',
                'time capsule moment': 'time-capsule'
            };
            
            exhibitId = textMappings[lowerData] || null;
        }
        
        if (exhibitId) {
            // Close the scanner modal
            handleAnimatedClose();
            dispatch(closeScanner());

            // Check if this is a zone-based exhibit that needs automatic zone claiming
            const zoneBasedExhibits = ['orang-laut', 'market-traders'];
            const isZoneBased = zoneBasedExhibits.includes(exhibitId);

            if (isZoneBased) {
                // For zone-based exhibits, navigate with fromQrScan parameter and timestamp
                // The timestamp ensures the useEffect triggers even for repeated scans of the same QR code
                const timestamp = Date.now().toString();
                navigate(`/exhibit/${exhibitId}?fromQrScan=true&qrTimestamp=${timestamp}`);
            } else {
                // For non-zone exhibits, navigate normally
                navigate(`/exhibit/${exhibitId}`);
            }
            return;
        }
        
        // If we get here, it's not a valid Time Capsule QR code
        setError(`Invalid QR code.`);
        
        // Restart scanning after a delay
        setTimeout(() => {
            if (scanner) {
                setError(null);
                scanner.start();
            }
        }, 3000);
    }, [scanner, handleAnimatedClose, dispatch, navigate, t]);

    // Initialize scanner when modal opens
    useEffect(() => {
        if (isOpen && videoRef.current && !scanner) {
            try {
                // Initialize QR scanner
                const newScanner = new QrScanner(
                    videoRef.current,
                    result => {
                        handleScan(result.data);
                    },
                    {
                        returnDetailedScanResult: true,
                        highlightScanRegion: true,
                        highlightCodeOutline: true,
                        preferredCamera: 'environment',
                    }
                );
                
                setScanner(newScanner);
                newScanner.start().then(() => {
                    setScanning(true);
                }).catch(err => {
                    setError(t('qrScanner.cameraError'));
                    console.error(err);
                });
            } catch (err) {
                setError(t('qrScanner.initError'));
                console.error(err);
            }
        }
        
        // Cleanup scanner when modal closes
        if (!isOpen && scanner) {
            scanner.destroy();
            setScanner(null);
            setScanning(false);
            setError(null);
        }
    }, [isOpen, scanner, t, handleScan]);

    // Close modal on Escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                handleAnimatedClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }
        
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, handleAnimatedClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);
    
    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            handleAnimatedClose();
        }
    };

    if (!shouldRender) return null;

    return (
        <div 
            className={`
                fixed inset-0 z-50 bg-black transition-opacity duration-300 ease-in-out
                ${isVisible ? 'bg-opacity-75 opacity-100' : 'bg-opacity-0 opacity-0'}
                flex items-end sm:items-center justify-center p-4
            `}
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="qr-scanner-modal-title"
        >
            {/* Modal Content */}
            <div 
                className={`
                    relative bg-black/40 backdrop-blur-xl border border-rose-600/70 rounded-[36px] overflow-hidden shadow-2xl w-full max-w-lg mx-auto
                    transform transition-all duration-300 ease-out
                    ${isVisible 
                        ? 'translate-y-0 opacity-100 scale-100' 
                        : 'translate-y-full opacity-0 scale-95'
                    }
                    sm:translate-y-0
                    before:absolute before:inset-0 before:rounded-xl before:bg-gradient-to-br before:from-white/10 before:to-transparent before:pointer-events-none
                `}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header with close button */}
                <div className={`flex items-center p-4 border-b border-white/10 ${showCloseButton ? 'justify-between' : 'justify-center'} relative z-10`}>
                    <h2 id="qr-scanner-modal-title" className="text-xl font-semibold text-white">
                        {t('qrScanner.title')}
                    </h2>
                    {showCloseButton && (
                        <button
                            onClick={handleAnimatedClose}
                            className="text-white/70 hover:text-white transition-colors p-1"
                            aria-label={t('common.close')}
                        >
                            <X size={24} />
                        </button>
                    )}
                </div>

                {/* Scanner Content */}
                <div className="p-6 relative z-10">
                    {error && (
                        <div className="mb-4 p-3 bg-red-500/20 border border-red-400/50 text-red-200 rounded-xl backdrop-blur-sm break-all text-sm">
                            {error}
                        </div>
                    )}
                    
                    <div className="relative mx-auto mb-4">
                        <div className="w-[70vw] h-[70vw] max-w-full max-h-[512px] mx-auto overflow-hidden rounded-xl border-2 border-white/30 bg-black/50 backdrop-blur-sm shadow-inner">
                            <video 
                                ref={videoRef} 
                                className="w-full h-full object-cover rounded-xl overflow-hidden"
                                playsInline
                                muted
                            />
                        </div>
                        
                        {scanning && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="w-[60vw] h-[60vw] max-w-full max-h-full border-2 border-white/50 opacity-70 animate-pulse rounded-xl shadow-lg"></div>
                            </div>
                        )}
                    </div>
                    
                    <p className="text-center text-white/80 text-sm">
                        {t('qrScanner.instructions')}
                    </p>
                </div>

                {/* Footer */}
                <div className="p-4 text-center border-t border-white/10 bg-white/5 rounded-b-lg relative z-10">
                    <button 
                        onClick={handleAnimatedClose}
                        className={`${isVisible ? 'w-full' : 'w-1/4'} bg-rose-600/70 text-white py-3 px-6 rounded-full transition-all duration-500 backdrop-blur-sm uppercase`}
                    >
                        {t('common.close')}
                    </button>
                </div>
            </div>
        </div>
    );
} 